import { useEffect, useState } from "react";
import { Crown, Check, Loader2, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    key: "free",
    name: "Starter",
    price: 0,
    tagline: "Free forever",
    features: [
      "15 voice/photo entries per month",
      "Unlimited text chat bookkeeping",
      "Dashboard, debtors & inventory",
      "1 active loan at a time",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    price: 1500,
    tagline: "Most popular",
    highlight: true,
    features: [
      "Unlimited voice + photo entries",
      "Shareable Daily Diary (PNG)",
      "Weekly & Monthly report PNGs",
      "Public payment page (@cashtag)",
      "Priority support",
    ],
  },
  {
    key: "business",
    name: "Business",
    price: 5000,
    tagline: "For growing operations",
    features: [
      "Everything in Pro",
      "Higher loan limits (₦500k+)",
      "Flexible repayment (weekly/monthly)",
      "Dedicated account manager",
      "Coming soon: multi-staff & API",
    ],
  },
];

export default function Pricing() {
  const nav = useNavigate();
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/subscription/me").then((r) => setCurrent(r.data));
  }, []);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const r = await api.post("/subscription/subscribe", { plan });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail?.slice(0, 100) || "Failed to start subscription");
      setBusy(null);
    }
  };

  return (
    <div className="px-4 py-5 space-y-5 pb-6">
      <div className="flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-accent flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
        <h1 className="font-heading text-xl font-bold">Upgrade your plan</h1>
      </div>

      {current?.is_premium && (
        <div className="bg-accent border border-kudi-green/30 rounded-2xl p-4 flex items-center gap-3">
          <Crown className="w-5 h-5 text-kudi-green" />
          <div>
            <div className="font-heading font-semibold text-sm">You're on {current.subscription?.plan?.toUpperCase()}</div>
            <div className="text-xs text-kudi-ink/60">Active subscription</div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {PLANS.map((p) => (
          <div
            key={p.key}
            className={`rounded-2xl p-5 border ${
              p.highlight
                ? "bg-gradient-to-br from-kudi-green to-kudi-green-dark text-white border-transparent shadow-lg"
                : "bg-white border-border"
            }`}
            data-testid={`plan-${p.key}`}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className={`text-xs font-semibold uppercase tracking-wider ${p.highlight ? "text-white/75" : "text-kudi-ink/55"}`}>{p.tagline}</div>
                <div className="font-heading text-2xl font-bold mt-1 flex items-center gap-2">
                  {p.name}
                  {p.highlight && <Crown className="w-5 h-5 text-amber-300" />}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-3xl font-bold tabular-nums">{p.price === 0 ? "Free" : fmtNaira(p.price)}</div>
                {p.price > 0 && <div className={`text-xs ${p.highlight ? "text-white/65" : "text-kudi-ink/55"}`}>/ month</div>}
              </div>
            </div>
            <ul className="mt-4 space-y-2">
              {p.features.map((f, i) => (
                <li key={i} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-white/90" : "text-kudi-ink/75"}`}>
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-white" : "text-kudi-green"}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {p.key !== "free" && (
              <button
                onClick={() => subscribe(p.key)}
                disabled={busy === p.key || current?.is_premium}
                className={`mt-5 w-full rounded-full py-3 font-semibold transition disabled:opacity-50 ${
                  p.highlight ? "bg-white text-kudi-green hover:bg-kudi-cream" : "bg-kudi-green text-white hover:bg-kudi-green-dark"
                }`}
                data-testid={`subscribe-${p.key}`}
              >
                {busy === p.key ? <><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Starting...</> :
                 current?.is_premium ? "Already subscribed" : `Get ${p.name}`}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="text-center text-xs text-kudi-ink/50">
        Powered by Paystack · Cancel anytime · Naija test mode active
      </div>
    </div>
  );
}
